/**
 * L'écran du planning — la semaine, les camions, et ce qu'ils font.
 *
 * Copie de l'écran de la v1, validée sur maquette le 04/09/2026, avec les six
 * changements demandés le même jour : le chauffeur en information principale,
 * aucune barre de chiffres au-dessus des journées, plus d'étiquette de service,
 * l'ordre des lignes réglable, cinq jours par défaut et un seul filtre.
 *
 * LA DÉCOUPE EST CELLE DES AUTRES ÉCRANS : `VuePlanning` dessine et ne décide
 * rien, `EcranPlanning` tient l'état et parle à l'API. Tout ce qui se calcule
 * — la semaine, les blocs, les refus — est dans `planning.ts`, éprouvé sans
 * navigateur.
 *
 * LE GLISSER-DÉPOSER EST LE SEUL GESTE D'AFFECTATION, comme en v1 : on prend un
 * ordre dans la pile ou dans la grille, on le pose sur une case. Il ne marche
 * pas au doigt et ne s'atteint pas au clavier ; c'est dit une fois ici et ce
 * n'est pas un oubli, c'est ce qui a été demandé.
 *
 * UN CLIC SUR LE BLOC ouvre la fiche de l'ordre. C'est le seul endroit où cet
 * écran en quitte un autre : il rend l'identifiant à l'application, qui bascule
 * sur les ordres de transport et l'y ouvre.
 *
 * Défaut constaté dans le navigateur de l'utilisateur le 04/09/2026 : seul un
 * chevron de treize pixels, gris clair, ouvrait la fiche. Il fonctionnait —
 * mesuré, clic par clic — mais personne ne le voyait, et le geste naturel,
 * cliquer sur le bloc, ne faisait rien. Le bloc entier est donc devenu la cible.
 * Le chevron reste : c'est lui qui se voit, et c'est le seul chemin au clavier,
 * un `div` cliquable n'étant pas atteignable par tabulation.
 *
 * TROIS CHOSES QUE CET ÉCRAN NE FAIT PAS
 * ---------------------------------------------------------------------------
 *   - il ne crée ni ordre ni étape : le planning pose un camion sur ce qui
 *     existe déjà ;
 *   - il n'affiche aucun montant. La v1 met le résultat de la journée en haut
 *     de chaque case ; la v2 n'a pas de coût de revient, et la barre a été
 *     retirée à la demande de l'utilisateur.
 */

import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import {
  affecterAuPlanning,
  lirePlanning,
  rangerLaCase,
  rangerLesVehicules,
  type EchecApi,
} from './api.js';
import {
  abregesDe,
  blocsParCase,
  colonneDite,
  correspond,
  empechementDe,
  casesFiltrees,
  decaler,
  finDeSemaine,
  groupesFiltres,
  instantDit,
  ordreApres,
  ordreDeLaCaseApres,
  pile,
  refusDepot,
  refusLiberation,
  reportDemande,
  semaineDe,
  semaineDite,
  teteDeLigne,
  type Bloc,
  type CartePile,
  type LigneBloc,
  type Planning,
  type Prise,
  type Semaine,
  type VehiculePlanning,
} from './planning.js';
import { PlanningImprime } from './PlanningImprimable.js';

/** Le jour d'aujourd'hui, dans le fuseau de celui qui regarde. */
function aujourdhui(): string {
  const maintenant = new Date();
  const mois = String(maintenant.getMonth() + 1).padStart(2, '0');
  const jour = String(maintenant.getDate()).padStart(2, '0');
  return `${maintenant.getFullYear()}-${mois}-${jour}`;
}

type Etat =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'pret'; readonly planning: Planning }
  | { readonly nom: 'panne' };

/** Ce que l'API a refusé, dit en français. */
const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable: 'Cet ordre n’existe plus. Rechargez l’écran.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  code_pris: 'Cette valeur est déjà prise.',
  invalide: 'Ce que l’écran a envoyé n’a pas été accepté. Rechargez et réessayez.',
  conflit: 'Deux périodes se recouvrent.',
  encore_actif: 'Désactivez d’abord.',
  encore_rattache: 'Impossible : quelque chose s’y rattache encore.',
  ordre_annule: 'Cet ordre est annulé : il ne se planifie pas.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape : rien à poser sur un camion.',
  etape_validee:
    'Une étape de cet ordre est déjà validée : ses dates ne se déplacent plus, et son camion ne se retire plus.',
  jour_sans_etape: 'Cet ordre n’a aucune étape ce jour-là. Rechargez l’écran.',
  // LE REFUS DES TOURNÉES, ajouté le 04/09/2026. Le numéro de la tournée est
  // LE NUMÉRO DE LA TOURNÉE ARRIVE DEPUIS LE 05/09/2026, et cette phrase-ci
  // n'est plus que le repli : elle sert quand le bloc n'en porte pas un seul —
  // deux tournées dans la même case, ou un dépôt venu de la pile. Le numéro ne
  // vient pas de l'API, dont `echec()` ne garde que le code : il vient du bloc,
  // que l'écran a déjà sous la main.
  etape_en_tournee:
    'Une étape de cet ordre appartient à une tournée. Sortez-l’en depuis l’écran des tournées avant de la déplacer.',
  // LES DEUX REFUS DU BOUCLAGE D'UNE JOURNÉE, ajoutés le 14/09/2026. Cette
  // carte doit rester EXHAUSTIVE : c'est elle qui garantit qu'aucun refus de
  // l'API ne s'affichera un jour sous forme de code brut.
  journee_cloturee:
    'Cette journée est clôturée. Rouvrez-la avant de la corriger.',
  journee_sans_chainage:
    'La journée précédente de ce camion ne dit pas où il s’est arrêté : renseignez son lieu de fin d’abord.',
  litige_deja_ouvert:
    'Cette étape a déjà un litige ouvert : résolvez-le avant d’en ouvrir un autre.',
  litige_deja_resolu:
    'Ce litige est déjà résolu. Ouvrez-en un nouveau sur l’étape si l’incident se répète.',
  // LES SIX REFUS DE LA FACTURATION, ajoutés le 09/09/2026. Ils n'arrivent pas
  // sur cet écran — la carte reste exhaustive, c'est ce qui garantit qu'aucun
  // refus de l'API ne s'affichera un jour sous forme de code brut.
  litige_ouvert: 'Cet ordre porte un litige ouvert : il ne se facture pas.',
  deja_facture: 'Cet ordre est déjà porté par une facture.',
  rien_a_facturer: 'Aucun de ces ordres n’attend de facture.',
  facture_emise: 'Cette pièce est émise : elle se corrige par un avoir.',
  facture_brouillon: 'Un brouillon ne s’envoie pas : validez-le d’abord.',
  facture_vide: 'Une facture sans ligne ne se valide pas.',
  // AJOUTÉ LE 09/09/2026. Un ordre parti en facturation ne se modifie plus :
  // il faut le retirer de la facturation d'abord.
  ordre_en_facturation:
    'Cet ordre est parti en facturation : retirez-le de la facturation avant de le modifier.',
  // LES CINQ REFUS DU RECOUVREMENT, ajoutés le 10/09/2026. Chacun dit la SUITE,
  // pas seulement le refus : c'est ce qui distingue un état d'une saisie fautive.
  affectation_excessive:
    'Vous affectez plus que ce qui a été reçu : corrigez la ventilation.',
  piece_non_emise:
    'Cette pièce est un brouillon : validez-la avant de la régler ou de la relancer.',
  piece_autre_client: 'Cette pièce appartient à un autre client.',
  gel_deja_actif:
    'Ce client a déjà un gel en cours : levez-le avant d’en poser un autre.',
  gel_absent: 'Ce client n’a aucun gel en cours.',
  // LES DEUX REFUS DE L'ANNULATION DE VALIDATION, ajoutés le 10/09/2026.
  numero_pas_le_dernier:
    'Cette pièce ne porte pas le dernier numéro de sa série : lui rendre son numéro laisserait un trou. Corrigez-la par un avoir.',
  facture_reglee:
    'Un règlement est affecté à cette pièce : détachez-le avant d’annuler la validation.',
  // AJOUTÉ LE 10/09/2026, sur un 500 constaté EN PRODUCTION : la base
  // refusait déjà (relance_pieces pointe sur factures en `on delete
  // restrict`), mais son refus sortait en erreur interne et l'écran
  // parlait d'une panne.
  piece_relancee:
    'Une relance a été envoyée sur cette pièce : elle ne peut plus être supprimée. Corrigez-la par un avoir.',
  facture_sans_echeance:
    'Cette pièce n’a pas de date d’échéance : sans elle, le recouvrement ne saura jamais qu’elle est en retard.',
  // AJOUTÉ LE 11/09/2026 avec le rattachement Pennylane. Distinct de
  // `code_pris` : le geste de réparation n'est pas le même. Le message NOMME
  // ce qu'il faut regarder — sans quoi il faudrait ouvrir les neuf autres
  // fiches pour trouver celle qui tient déjà ce client.
  pennylane_deja_rattache:
    'Ce client Pennylane est déjà rattaché à une autre fiche de l’annuaire. Le plus souvent, c’est que deux fiches Stafa désignent le même client : vérifiez vos doublons.',
  // AJOUTÉS LE 11/09/2026 avec l'envoi d'une facture chez Pennylane. Deux
  // messages et pas un : le premier mène à un AUTRE ÉCRAN, le second demande
  // de corriger la pièce. L'écran ne propose pas la même suite.
  pennylane_client_non_rattache:
    'Le client de cette facture n’est pas rattaché à un client Pennylane. Allez dans Paramètres → Pennylane pour le rattacher, puis réessayez.',
  pennylane_impossible:
    'Cette facture n’a pas pu être envoyée chez Pennylane. Le détail est affiché sous ce message : il vient de Pennylane et dit ce qui ne va pas.',
  facture_pas_deposee:
    'Cette facture n\u2019est pas encore d\u00e9pos\u00e9e chez Pennylane : envoyez-la d\u2019abord.',
  facture_modifiee_depuis_apercu:
    'Cette facture a changé depuis le dernier aperçu. Recliquez « Aperçu Pennylane », vérifiez le document, puis validez.',
  // LES QUATRE REFUS DE L'AVOIR, ajoutés le 11/09/2026. Quatre et pas un :
  // chacun porte une SUITE différente — ouvrir l'avoir existant, corriger un
  // montant, ou rien du tout quand le geste n'a pas de sens.
  avoir_sur_avoir:
    'On n’établit pas un avoir sur un avoir. Si cet avoir est faux, c’est une facture qu’il faut établir.',
  facture_deja_avoiree:
    'Cette facture est déjà intégralement avoirée : un second avoir rendrait au client plus qu’il n’a payé. Ouvrez l’avoir existant.',
  avoir_trop_grand:
    'Cet avoir dépasse ce qui reste à avoirer sur cette facture. Le détail affiché sous ce message donne le montant maximal.',
  avoir_impossible:
    'Cet avoir ne peut pas être établi. Le détail affiché sous ce message dit ce qui s’y oppose.',
  // AJOUTÉ LE 10/09/2026. Distinct de `injoignable`, qui parlait de connexion
  // alors que le service avait bel et bien répondu.
  service:
    'Le service a répondu une erreur inattendue. Ce n’est pas votre connexion. Ouvrez la console (F12) : le code y est écrit.',
};

/** Le report en attente de réponse : on a lâché sur un autre jour. */
interface Report {
  readonly prise: Prise;
  readonly vehiculeId: string;
  readonly jour: string;
}

// ---------------------------------------------------------------------------
// Le dessin
// ---------------------------------------------------------------------------

function LignesDuBloc({ lignes }: { readonly lignes: readonly LigneBloc[] }) {
  return (
    <>
      {lignes.map((ligne) => (
        <div
          key={ligne.id}
          className={ligne.autre ? 'ligne-etape autre' : 'ligne-etape'}
        >
          <span className={ligne.chargement ? 'sens chargement' : 'sens livraison'}>
            {ligne.chargement ? '▼' : '▲'}
          </span>
          <span className="lieu">
            {ligne.site}
            {ligne.departement === null ? null : (
              <span className="departement"> {ligne.departement}</span>
            )}
          </span>
          <span className="heure">
            {ligne.autre ? `${ligne.jour.slice(8)}/${ligne.jour.slice(5, 7)} ` : ''}
            {ligne.heure ?? ''}
          </span>
          <span className={ligne.litige ? 'marque litige' : 'marque'}>
            {ligne.litige ? '⚠' : ligne.fait ? '✓' : ''}
          </span>
          {ligne.marchandise === null ? null : (
            <span className="quantite">{ligne.marchandise}</span>
          )}
        </div>
      ))}
    </>
  );
}

export interface ProprietesVuePlanning {
  readonly etat: Etat;
  readonly semaine: Semaine;
  readonly septJours: boolean;
  readonly quandSeptJours: (sept: boolean) => void;
  readonly quandSemaine: (jour: string) => void;
  readonly recherche: string;
  readonly quandRecherche: (texte: string) => void;
  readonly refus: string | null;
  readonly cible: string | null;
  readonly prise: Prise | null;
  readonly quandPrise: (prise: Prise | null) => void;
  readonly quandCible: (cle: string | null) => void;
  readonly quandDepot: (vehiculeId: string, jour: string) => void;
  readonly quandLibere: () => void;
  readonly ligneSaisie: string | null;
  readonly quandLigneSaisie: (id: string | null) => void;
  readonly quandRanger: (avant: string | null) => void;
  /**
   * RANGER LES BLOCS D'UNE CASE — 16/09/2026. `avant` est l'ordre devant lequel
   * poser celui qu'on tient, ou `null` pour le mettre en dernier.
   */
  readonly quandRangerLaCase: (
    vehiculeId: string,
    jour: string,
    avant: string | null,
  ) => void;
  /** Ouvrir la fiche d'un ordre, dans l'écran des ordres de transport. */
  readonly quandOuvrirOrdre: (otId: string) => void;
  /**
   * L'APERÇU D'IMPRESSION, DÉJÀ OUVERT — lot 122, et il n'est là que pour
   * l'aperçu de maquette.
   *
   * C'EST LA LEÇON DU LOT 121, ÉCRITE EN CODE. Le défaut d'impression du
   * 23/10/2026 — une feuille décalée, des pages blanches au-dessus — n'avait
   * pas été vu parce que l'aperçu ne montrait QUE la feuille, sans l'écran
   * derrière. Il ne pouvait donc pas reproduire la situation qui produisait le
   * défaut.
   *
   * Cette propriété ouvre le voile sur l'écran COMPLET, avec le planning
   * dessous, et avec un instant figé pour que deux captures se comparent. En
   * usage réel elle reste absente, et le bouton fait le travail.
   */
  readonly impressionOuverte?: string | null;
}

export function VuePlanning({
  etat,
  semaine,
  septJours,
  quandSeptJours,
  quandSemaine,
  recherche,
  quandRecherche,
  refus,
  cible,
  prise,
  quandPrise,
  quandCible,
  quandDepot,
  quandLibere,
  ligneSaisie,
  quandLigneSaisie,
  quandRanger,
  quandRangerLaCase,
  quandOuvrirOrdre,
  impressionOuverte,
}: ProprietesVuePlanning) {
  // OUVRIR ET GLISSER COMMENCENT PAR LE MÊME APPUI. Ce drapeau les distingue :
  // posé au départ d'un glisser, remis à zéro au début de l'appui suivant. Sans
  // lui, poser un ordre sur un camion ouvrirait sa fiche dans la foulée sur les
  // navigateurs qui envoient un clic après un dépôt.
  //
  // Un `useRef` et non un état : le changer ne doit rien redessiner.
  const aGlisse = useRef(false);

  /**
   * L'INTERVALLE VISÉ pendant un glissement — 16/09/2026.
   *
   * UN ÉTAT ET NON UN `useRef`, contrairement au drapeau ci-dessus : celui-ci
   * DOIT redessiner, puisque c'est lui qui allume le trait de dépôt. Sans quoi
   * on glisserait à l'aveugle.
   *
   * Sa clé est `camion|jour|ordre` : l'intervalle qui précède ce bloc-là.
   */
  const [intervalle, setIntervalle] = useState<string | null>(null);

  /**
   * LA FEUILLE IMPRIMABLE, ouverte ou non — lot 122.
   *
   * L'ÉTAT EST L'INSTANT D'IMPRESSION, et non un booléen : il est figé à
   * l'ouverture et ne bouge plus. Un `new Date()` relu à chaque dessin aurait
   * fait changer l'heure sous les yeux de celui qui relit sa feuille avant
   * d'appuyer sur Imprimer — et le papier n'aurait pas porté l'heure annoncée
   * à l'écran.
   */
  const [imprime, setImprime] = useState<string | null>(impressionOuverte ?? null);

  /**
   * LE JOUR MONTRÉ SUR TÉLÉPHONE — lot 133, maquette du 01/11/2026.
   *
   * Sur 390 px, une semaine de cinq colonnes ne se lit pas : le téléphone
   * montre UN jour, choisi dans une bande de pastilles. `null` = le jour par
   * défaut, aujourd'hui s'il est dans la semaine affichée, sinon le premier.
   * Un état local, comme `imprime` : il ne regarde que l'écran.
   */
  const [jourTel, setJourTel] = useState<string | null>(null);
  /**
   * L'AFFECTATION AU DOIGT — lot 133. Le glisser-déposer ne marche pas sur
   * un écran tactile : « Affecter » prend l'ordre (`quandPrise`, le même que
   * le début d'un glisser) et ouvre la liste des camions ; choisir un camion
   * fait le même dépôt que lâcher le bloc dans sa case (`quandDepot`). Les
   * mêmes gardes et la même demande de report s'appliquent donc.
   */
  const [affecterTel, setAffecterTel] = useState<string | null>(null);

  if (etat.nom === 'chargement') {
    return <p className="attente">Chargement du planning…</p>;
  }
  if (etat.nom === 'panne') {
    return (
      <p className="attente">
        Stafa ne répond pas. Vérifiez votre connexion et rechargez la page.
      </p>
    );
  }

  // Les abrégés viennent de la société, avec le planning lui-même.
  const abreges = abregesDe(etat.planning.supports);
  const cases = blocsParCase(etat.planning.etapes, abreges);
  const groupes = pile(etat.planning.a_affecter, abreges);
  // FACULTATIF DANS LE TYPE, et c'est voulu : une API déployée avant ce
  // correctif ne renvoie pas la clé, et l'écran doit continuer de fonctionner
  // plutôt que de tomber sur un `undefined.find`.
  const indisponibilites = etat.planning.indisponibilites ?? [];
  const ce = aujourdhui();

  const prendre = (
    otId: string,
    numero: string,
    jour: string | null,
    debut: string,
    statut: string,
    lignes: readonly LigneBloc[],
    tournee: string | null = null,
  ): Prise => ({
    ot_id: otId,
    numero,
    jour,
    debut,
    statut,
    valide: lignes.some((l) => l.fait),
    tournee,
  });

  /**
   * Ce qui rend un bloc OUVRABLE, en plus de déplaçable.
   *
   * Posé sur le bloc entier plutôt que sur le seul chevron : c'est là que la
   * main va. L'appui remet le drapeau à zéro, le glisser le pose, et le clic
   * qui suit un glisser est donc ignoré — on vient de déplacer l'ordre, pas de
   * demander à le lire.
   */
  const ouvrable = (otId: string) => ({
    title: 'Ouvrir cet ordre de transport',
    onMouseDown: () => {
      aGlisse.current = false;
    },
    onClick: () => {
      if (aGlisse.current) return;
      quandOuvrirOrdre(otId);
    },
  });

  return (
    <div className="planning">
      <div className="planning-barre">
        <button
          className="bouton bouton-discret"
          type="button"
          onClick={() => quandSemaine(decaler(semaine.du, -7) as string)}
          aria-label="Semaine précédente"
        >
          ‹
        </button>
        <button
          className="bouton bouton-discret"
          type="button"
          onClick={() => quandSemaine(decaler(semaine.du, 7) as string)}
          aria-label="Semaine suivante"
        >
          ›
        </button>
        <button
          className="bouton bouton-discret"
          type="button"
          onClick={() => quandSemaine(ce)}
        >
          Aujourd’hui
        </button>
        <span className="bascule">
          <button
            className={septJours ? 'bouton bouton-discret' : 'bouton bouton-choisi'}
            type="button"
            onClick={() => quandSeptJours(false)}
          >
            5 j
          </button>
          <button
            className={septJours ? 'bouton bouton-choisi' : 'bouton bouton-discret'}
            type="button"
            onClick={() => quandSeptJours(true)}
          >
            7 j
          </button>
        </span>
        <span className="titre-semaine">
          {semaineDite(semaine.du, semaine.au) ?? ''}
        </span>
        <input
          className="champ recherche"
          type="search"
          value={recherche}
          placeholder="Chercher : ordre, client, ville"
          onChange={(evenement) => quandRecherche(evenement.target.value)}
        />
        {/* L'IMPRESSION — lot 122. Le bouton est le dernier de la barre parce
            qu'il ne se presse qu'une fois le planning réglé : on choisit sa
            semaine, on cherche s'il le faut, PUIS on imprime. */}
        <button
          className="bouton"
          type="button"
          title="Imprimer le planning de la semaine affichée"
          onClick={() => setImprime(instantDit(new Date()))}
        >
          Imprimer
        </button>
      </div>

      <div className="planning-legende">
        <span>
          <i className="pastille attente" />à affecter
        </span>
        <span>
          <i className="pastille planifie" />
          planifié
        </span>
        <span>
          <i className="pastille encours" />
          en cours
        </span>
        <span>
          <i className="pastille livre" />
          livré
        </span>
        <span>
          <i className="pastille litige" />
          litige
        </span>
        <span className="sens chargement">▼ chargement</span>
        <span className="sens livraison">▲ livraison</span>
        <span className="marque-legende">✓ validée</span>
        <span className="aide">
          Glissez un ordre sur un camion pour l’affecter — ramenez-le à gauche pour le
          libérer.
        </span>
      </div>

      {refus === null ? null : <p className="bandeau-refus">{refus}</p>}

      {/* LE PLANNING DU TÉLÉPHONE — lot 133. Montré sous 640 px à la place de
          la pile et de la grille (styles.css). Mêmes données, mêmes blocs,
          mêmes gestes, un jour à la fois. */}
      {(() => {
        const jourVu =
          jourTel !== null && semaine.jours.includes(jourTel)
            ? jourTel
            : semaine.jours.includes(ce)
              ? ce
              : (semaine.jours[0] as string);
        const cartesPile = groupes.flatMap((g) =>
          g.cartes.filter((carte) => correspond(carte, recherche)),
        );
        const enMain =
          affecterTel === null ? undefined : cartesPile.find((c) => c.ot_id === affecterTel);
        return (
          <div className="planning-tel">
            <div className="jours-tel" role="group" aria-label="Jour">
              {semaine.jours.map((jour) => {
                const [nom, date] = (colonneDite(jour) ?? jour).split(' ');
                return (
                  <button
                    key={jour}
                    type="button"
                    className="jour-tel"
                    aria-pressed={jour === jourVu}
                    onClick={() => setJourTel(jour)}
                  >
                    {nom}
                    <b>{date?.slice(0, 2)}</b>
                  </button>
                );
              })}
            </div>

            {cartesPile.length > 0 && (
              <>
                <h2 className="titre-tel">À affecter · {cartesPile.length}</h2>
                {cartesPile.map((carte) => (
                  <div key={carte.ot_id} className="carte-tel a-affecter">
                    <button
                      type="button"
                      className="entete-tel"
                      onClick={() => quandOuvrirOrdre(carte.ot_id)}
                    >
                      <b>{carte.numero} ›</b>
                      <span>{carte.client}</span>
                    </button>
                    <LignesDuBloc lignes={carte.lignes} />
                    <button
                      type="button"
                      className="bouton affecter-tel"
                      onClick={() => {
                        quandPrise(
                          prendre(carte.ot_id, carte.numero, null, carte.du, carte.statut, carte.lignes),
                        );
                        setAffecterTel(carte.ot_id);
                      }}
                    >
                      Affecter
                    </button>
                  </div>
                ))}
              </>
            )}

            <h2 className="titre-tel">Chauffeurs · {etat.planning.vehicules.length}</h2>
            {etat.planning.vehicules.map((vehicule) => {
              const tete = teteDeLigne(vehicule);
              const blocs = (cases.get(`${vehicule.id}|${jourVu}`) ?? []).filter((bloc) =>
                correspond(bloc, recherche),
              );
              const empeche = empechementDe(vehicule, jourVu, indisponibilites);
              return (
                <section key={vehicule.id} className="camion-tel">
                  <div className="tete-camion-tel">
                    <span className={tete.sansChauffeur ? 'nom sans' : 'nom'}>{tete.principal}</span>
                    <span className="materiel">{tete.materiel}</span>
                  </div>
                  {empeche === null ? null : (
                    <div className="mention-empeche">
                      {empeche.motif}
                      {empeche.quoi === 'chauffeur' ? '' : ` · ${empeche.nom}`}
                    </div>
                  )}
                  {blocs.length === 0 ? (
                    <p className="vide-tel">Aucun ordre ce jour</p>
                  ) : (
                    blocs.map((bloc) => (
                      <button
                        key={`${bloc.ot_id}|${bloc.jour}`}
                        type="button"
                        className={`bloc-ot ${bloc.classe}${bloc.litige ? ' litige' : ''}`}
                        onClick={() => quandOuvrirOrdre(bloc.ot_id)}
                      >
                        <span className="entete-bloc">
                          <span>
                            {bloc.suite_avant ? '‹ ' : ''}
                            {bloc.numero} ›
                          </span>
                          {bloc.tournee === null ? null : (
                            <span className="etiq-tournee">{bloc.tournee}</span>
                          )}
                        </span>
                        <LignesDuBloc lignes={bloc.lignes} />
                      </button>
                    ))
                  )}
                </section>
              );
            })}

            {enMain === undefined ? null : (
              <div
                className="voile-tel"
                role="presentation"
                onClick={() => {
                  setAffecterTel(null);
                  quandPrise(null);
                }}
              >
                <div
                  className="panneau-tel"
                  role="dialog"
                  aria-label={`Affecter l’ordre ${enMain.numero}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3>
                    Affecter {enMain.numero} · {colonneDite(enMain.du) ?? enMain.du}
                  </h3>
                  {etat.planning.vehicules.map((vehicule) => {
                    const tete = teteDeLigne(vehicule);
                    const empeche = empechementDe(vehicule, enMain.du, indisponibilites);
                    return (
                      <button
                        key={vehicule.id}
                        type="button"
                        className="choix-tel"
                        onClick={() => {
                          setAffecterTel(null);
                          // LE JOUR DE L'ORDRE, et non le jour regardé : une
                          // carte de la pile s'affecte telle qu'elle est. Pour
                          // la changer de jour, l'ordinateur et sa demande de
                          // report restent le geste.
                          quandDepot(vehicule.id, enMain.du);
                        }}
                      >
                        <span className="nom">{tete.principal}</span>
                        <span className={empeche === null ? 'materiel' : 'materiel empeche-tel'}>
                          {empeche === null ? tete.materiel : `${tete.materiel} · ${empeche.motif}`}
                        </span>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    className="bouton bouton-discret"
                    onClick={() => {
                      setAffecterTel(null);
                      quandPrise(null);
                    }}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <div className="planning-corps">
        <div
          className={cible === 'pile' ? 'pile-ot survol' : 'pile-ot'}
          onDragOver={(evenement) => {
            if (prise === null || prise.jour === null) return;
            evenement.preventDefault();
            quandCible('pile');
          }}
          onDragLeave={() => quandCible(null)}
          onDrop={(evenement) => {
            evenement.preventDefault();
            quandLibere();
          }}
        >
          <h2>
            À affecter
            <span>
              {(() => {
                const combien = groupes.reduce((t, g) => t + g.cartes.length, 0);
                return `${combien} ordre${combien > 1 ? 's' : ''}`;
              })()}
            </span>
          </h2>
          <div className="liste-pile">
            {groupes.length === 0 ? (
              <p className="vide">Tout est affecté.</p>
            ) : (
              groupes.map((groupe) => (
                <div key={groupe.titre}>
                  <p className="groupe-pile">{groupe.titre}</p>
                  {groupe.cartes
                    .filter((carte) => correspond(carte, recherche))
                    .map((carte: CartePile) => (
                      <div
                        key={carte.ot_id}
                        className={`bloc-ot ${carte.classe}${carte.litige ? ' litige' : ''}${
                          prise?.ot_id === carte.ot_id ? ' souleve' : ''
                        }`}
                        draggable
                        {...ouvrable(carte.ot_id)}
                        onDragStart={() => {
                          aGlisse.current = true;
                          quandPrise(
                            prendre(
                              carte.ot_id,
                              carte.numero,
                              null,
                              carte.du,
                              carte.statut,
                              carte.lignes,
                            ),
                          );
                        }}
                        onDragEnd={() => {
                          quandPrise(null);
                          quandCible(null);
                        }}
                      >
                        <div className="entete-bloc">
                          <span>{carte.numero}</span>
                          <button
                            className="ouvrir-ordre"
                            type="button"
                            title="Ouvrir cet ordre de transport"
                            onClick={(evenement) => {
                              // Le bloc écoute aussi : sans cela, ouvrir
                              // deux fois.
                              evenement.stopPropagation();
                              quandOuvrirOrdre(carte.ot_id);
                            }}
                          >
                            ›
                          </button>
                        </div>
                        <LignesDuBloc lignes={carte.lignes} />
                        <div className="client-bloc">{carte.client}</div>
                      </div>
                    ))}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="grille-planning">
          {/* LE NOMBRE DE JOURS EST DONNÉ À LA FEUILLE DE STYLE, et il n'y a
              pas d'autre moyen : le tableau est en largeurs imposées pour que
              les cinq colonnes soient égales, et c'est de ce nombre que dépend
              la largeur en dessous de laquelle il doit défiler plutôt que de
              se tasser. Cinq jours ou sept, la valeur suit. */}
          <table style={{ '--jours': String(semaine.jours.length) } as CSSProperties}>
            <thead>
              <tr>
                <th className="colonne-camion">Chauffeur</th>
                {semaine.jours.map((jour) => (
                  <th key={jour} className={finDeSemaine(jour) ? 'we' : undefined}>
                    {colonneDite(jour)}
                    {jour === ce ? <span className="point"> •</span> : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {etat.planning.vehicules.map((vehicule: VehiculePlanning) => {
                const tete = teteDeLigne(vehicule);
                return (
                  <tr key={vehicule.id}>
                    <td
                      className={
                        ligneSaisie === vehicule.id ? 'camion souleve' : 'camion'
                      }
                      onDragOver={(evenement) => {
                        if (ligneSaisie === null) return;
                        evenement.preventDefault();
                      }}
                      onDrop={(evenement) => {
                        if (ligneSaisie === null) return;
                        evenement.preventDefault();
                        quandRanger(vehicule.id);
                      }}
                    >
                      <span
                        className="poignee"
                        draggable
                        title="Glisser pour changer l’ordre des lignes"
                        onDragStart={() => quandLigneSaisie(vehicule.id)}
                        onDragEnd={() => quandLigneSaisie(null)}
                      >
                        ⠿
                      </span>
                      <div className={tete.sansChauffeur ? 'nom sans' : 'nom'}>
                        {tete.principal}
                      </div>
                      <div className="materiel">{tete.materiel}</div>
                    </td>

                    {semaine.jours.map((jour) => {
                      const cle = `${vehicule.id}|${jour}`;
                      const blocs = (cases.get(cle) ?? []).filter((bloc: Bloc) =>
                        correspond(bloc, recherche),
                      );
                      // CE QUI EMPÊCHE CE CAMION DE ROULER CE JOUR-LÀ, ajouté
                      // le 08/09/2026 : « mettre un chauffeur absent ne
                      // l'indique pas sur le planning ». La case se barre,
                      // mais elle NE SE BLOQUE PAS — l'exploitation sait des
                      // choses que le logiciel ignore, et un écran qui
                      // interdit se contourne en saisissant faux.
                      const empeche = empechementDe(vehicule, jour, indisponibilites);
                      /**
                       * CETTE CASE PEUT-ELLE ÊTRE RANGÉE MAINTENANT ?
                       *
                       * DÈS QU'ON TIENT UN BLOC — 17/09/2026. Rien d'autre.
                       *
                       * DEUX CONDITIONS SONT TOMBÉES CE JOUR-LÀ, et c'est
                       * Mohamed qui les a fait tomber : « on n'a jamais mis ce
                       * mécanisme en place ». Il avait tort sur le code et
                       * raison sur ce qu'il voyait — le rangement existait
                       * depuis le 16/09 et ne s'était JAMAIS déclenché chez
                       * lui, parce qu'il exigeait :
                       *
                       *   - `blocs.length > 1` : une case d'un seul ordre ne
                       *     s'ouvrait pas. J'avais écrit « ranger une case d'un
                       *     seul bloc n'a aucun sens ». C'est vrai du rangement
                       *     et FAUX de l'insertion : c'est exactement la case
                       *     où l'on veut poser un deuxième ordre à sa place ;
                       *   - le bloc devait DÉJÀ être dans la case. Poser un
                       *     ordre à une place précise sur un autre camion
                       *     demandait donc deux gestes — déposer, puis
                       *     reprendre — et personne ne devine le second.
                       *
                       * Une fonction qu'on ne voit jamais se déclencher n'est
                       * pas une fonction : c'est du code mort qui se croit
                       * vivant.
                       */
                      const rangeable = prise !== null;
                      return (
                        <td
                          key={jour}
                          className={`case-jour${finDeSemaine(jour) ? ' we' : ''}${
                            cible === cle ? ' visee' : ''
                          }${empeche === null ? '' : ' empechee'}`}
                          title={
                            empeche === null
                              ? undefined
                              : `${empeche.nom} — ${empeche.motif}${
                                  empeche.note === null ? '' : ` · ${empeche.note}`
                                }`
                          }
                          onDragOver={(evenement) => {
                            if (prise === null) return;
                            evenement.preventDefault();
                            quandCible(cle);
                          }}
                          onDragLeave={() => {
                            if (cible === cle) quandCible(null);
                          }}
                          onDrop={(evenement) => {
                            if (prise === null) return;
                            evenement.preventDefault();
                            quandDepot(vehicule.id, jour);
                          }}
                        >
                          {empeche === null ? null : (
                            <div className="mention-empeche">
                              {empeche.motif}
                              {empeche.quoi === 'chauffeur' ? '' : ` · ${empeche.nom}`}
                            </div>
                          )}
                          {/* PLUS DE MARQUE « RANGÉ À LA MAIN » — retirée le
                              17/09/2026 à la demande de Mohamed, le jour même
                              où le rangement s'est mis à servir.

                              ELLE DISAIT POURQUOI LA CASE NE SUIT PLUS LES
                              HEURES, et elle portait le geste inverse : un clic
                              rendait la journée au tri par heure. Les deux
                              partent ensemble, c'est ce qui a été choisi — une
                              case rangée le reste, et se réarrange à la souris.

                              CE QUI SE PERD, écrit ici pour qu'on ne le
                              redécouvre pas : il n'existe plus aucun moyen de
                              revenir au tri automatique d'une case. */}
                          {blocs.map((bloc) => (
                            <Fragment key={`${bloc.ot_id}|${bloc.jour}`}>
                              {/* L'INTERVALLE QUI PRÉCÈDE CE BLOC. Il
                                  n'apparaît que pendant un glissement, et
                                  SEULEMENT pour un bloc déjà dans cette case :
                                  un bloc venu d'ailleurs doit d'abord y être
                                  affecté, et c'est la case entière qui s'en
                                  charge — le geste que Mohamed connaît déjà. */}
                              {rangeable && (
                                <div
                                  className={`intervalle${
                                    intervalle === `${cle}|${bloc.ot_id}` ? ' vise' : ''
                                  }`}
                                  onDragOver={(evenement) => {
                                    evenement.preventDefault();
                                    evenement.stopPropagation();
                                    setIntervalle(`${cle}|${bloc.ot_id}`);
                                  }}
                                  onDragLeave={() => setIntervalle(null)}
                                  onDrop={(evenement) => {
                                    evenement.preventDefault();
                                    // SANS CECI la case recevrait le dépôt elle
                                    // aussi, et affecterait l'ordre par-dessus
                                    // le rangement.
                                    evenement.stopPropagation();
                                    setIntervalle(null);
                                    quandRangerLaCase(vehicule.id, jour, bloc.ot_id);
                                  }}
                                />
                              )}
                            <div
                              className={`bloc-ot ${bloc.classe}${
                                bloc.litige ? ' litige' : ''
                              }${bloc.suite_avant ? ' suite-avant' : ''}${
                                bloc.suite_apres ? ' suite-apres' : ''
                              }${prise?.ot_id === bloc.ot_id ? ' souleve' : ''}`}
                              draggable
                              {...ouvrable(bloc.ot_id)}
                              onDragStart={() => {
                                aGlisse.current = true;
                                quandPrise(
                                  prendre(
                                    bloc.ot_id,
                                    bloc.numero,
                                    bloc.jour,
                                    bloc.jour,
                                    bloc.statut,
                                    bloc.lignes,
                                    bloc.tournee,
                                  ),
                                );
                              }}
                              onDragEnd={() => {
                                quandPrise(null);
                                quandCible(null);
                              }}
                            >
                              <div className="entete-bloc">
                                <span>
                                  {bloc.suite_avant ? '‹ ' : ''}
                                  {bloc.numero}
                                </span>
                                {/* LE NUMÉRO DE TOURNÉE, décidé le 04/09/2026.
                                    Il dit aussi POURQUOI ce bloc ne se déplace
                                    pas : le planning refuse de bouger une étape
                                    en tournée, et sans ce numéro le refus
                                    n'indiquerait pas où aller la sortir. */}
                                {bloc.tournee === null ? null : (
                                  <span
                                    className="etiq-tournee"
                                    title={`Dans la tournée ${bloc.tournee} — sortez-la de sa tournée pour la déplacer`}
                                  >
                                    {bloc.tournee}
                                  </span>
                                )}
                                <button
                                  className="ouvrir-ordre"
                                  type="button"
                                  title="Ouvrir cet ordre de transport"
                                  onClick={(evenement) => {
                                    evenement.stopPropagation();
                                    quandOuvrirOrdre(bloc.ot_id);
                                  }}
                                >
                                  ›
                                </button>
                              </div>
                              <LignesDuBloc lignes={bloc.lignes} />
                              {bloc.repris_par === null ? null : (
                                <div className="relais">
                                  livré par <b>{bloc.repris_par}</b>
                                </div>
                              )}
                            </div>
                            </Fragment>
                          ))}
                          {/* L'INTERVALLE DE FIN : déposer ici met le bloc en
                              DERNIER. Sans lui, la dernière place de la journée
                              serait la seule qu'on ne saurait pas viser. */}
                          {rangeable && (
                            <div
                              className={`intervalle${
                                intervalle === `${cle}|fin` ? ' vise' : ''
                              }`}
                              onDragOver={(evenement) => {
                                evenement.preventDefault();
                                evenement.stopPropagation();
                                setIntervalle(`${cle}|fin`);
                              }}
                              onDragLeave={() => setIntervalle(null)}
                              onDrop={(evenement) => {
                                evenement.preventDefault();
                                evenement.stopPropagation();
                                setIntervalle(null);
                                quandRangerLaCase(vehicule.id, jour, null);
                              }}
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* LA FEUILLE SORT DANS `document.body` par un portail : elle est posée
          ici pour la lisibilité, elle n'y reste pas. */}
      {imprime === null ? null : (
        <PlanningImprime
          semaine={semaine}
          vehicules={etat.planning.vehicules}
          /* LES DONNÉES SONT FILTRÉES AVANT D'ÊTRE PASSÉES, et non au dessin
             comme le fait la grille : la feuille porte la mention « filtré
             sur », et un document qui annonce un extrait doit en être un. */
          cases={casesFiltrees(cases, recherche)}
          groupes={groupesFiltres(groupes, recherche)}
          indisponibilites={indisponibilites}
          recherche={recherche}
          imprimeLe={imprime}
          quandFermer={() => setImprime(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'état
// ---------------------------------------------------------------------------

export interface ProprietesPlanning {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  /** Demande à l'application d'ouvrir cet ordre dans l'écran des ordres. */
  readonly quandOuvrirOrdre: (otId: string) => void;
}

export function EcranPlanning({
  jeton,
  quandAccesPerdu,
  quandOuvrirOrdre,
}: ProprietesPlanning) {
  const [etat, setEtat] = useState<Etat>({ nom: 'chargement' });
  const [ancre, setAncre] = useState(aujourdhui());
  const [septJours, setSeptJours] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [refus, setRefus] = useState<string | null>(null);
  const [prise, setPrise] = useState<Prise | null>(null);
  const [cible, setCible] = useState<string | null>(null);
  const [ligneSaisie, setLigneSaisie] = useState<string | null>(null);
  const [report, setReport] = useState<Report | null>(null);

  const semaine = semaineDe(ancre, septJours) as Semaine;

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  /**
   * Toute la semaine en un seul appel.
   *
   * Le jour de RÉFÉRENCE est aujourd'hui quand la semaine le contient, et le
   * premier jour sinon : c'est lui qui décide quel chauffeur s'affiche en tête
   * de ligne. Sans cela, en regardant la semaine prochaine on lirait l'attelage
   * d'un lundi qui n'est pas à l'écran.
   */
  const charger = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;

    const ce = aujourdhui();
    const reference = ce >= semaine.du && ce <= semaine.au ? ce : semaine.du;
    const lu = await lirePlanning(j, {
      du: semaine.du,
      au: semaine.au,
      reference,
    });
    if (lu.etat !== 'ok') {
      if (lu.cause === 'acces') quandAccesPerdu();
      setEtat({ nom: 'panne' });
      return;
    }
    setEtat({ nom: 'pret', planning: lu.valeur });
  }, [avecJeton, quandAccesPerdu, semaine.du, semaine.au]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /** Une écriture, puis un rechargement : la base a le dernier mot. */
  const ecrire = (
    action: (j: string) => Promise<{ etat: 'ok' } | { etat: 'echec'; cause: EchecApi }>,
    /**
     * La tournée d'où part le bloc, quand on la connaît. **Elle ne vient pas de
     * l'API** : `echec()` ne garde que le code, et l'écran la tient déjà — le
     * bloc la porte depuis le 05/09/2026. C'est ce qui transforme « une étape
     * de cet ordre appartient à une tournée » en une phrase qui dit où aller.
     */
    tournee: string | null = null,
  ) => {
    setRefus(null);
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await action(j);
      if (resultat.etat === 'ok') {
        await charger();
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setRefus(
        resultat.cause === 'etape_en_tournee' && tournee !== null
          ? `Une étape de cet ordre appartient à la tournée ${tournee}. Sortez-l’en depuis l’écran de préparation avant de la déplacer.`
          : REFUS[resultat.cause],
      );
    })();
  };

  /**
   * Poser le camion, avec ou sans report.
   *
   * SANS report, `jour` est celui de la case d'où part le bloc — `null` pour
   * une carte de la pile, ce qui affecte l'ordre entier. AVEC report, l'API
   * exige le jour de départ : une carte de la pile donne alors le premier jour
   * de l'ordre, sans quoi le décalage n'aurait pas d'origine.
   */
  const poser = (prisePosee: Prise, vehiculeId: string, versLe: string | null) => {
    const depart = prisePosee.jour ?? prisePosee.debut;
    ecrire(
      (j) =>
        affecterAuPlanning(j, {
          ot_id: prisePosee.ot_id,
          jour: versLe === null ? prisePosee.jour : depart,
          vehicule_id: vehiculeId,
          vers_le: versLe,
        }),
      prisePosee.tournee,
    );
    setPrise(null);
    setCible(null);
  };

  return (
    <main className="corps corps-planning">
      <VuePlanning
        etat={etat}
        semaine={semaine}
        septJours={septJours}
        quandSeptJours={setSeptJours}
        quandSemaine={setAncre}
        recherche={recherche}
        quandRecherche={setRecherche}
        refus={refus}
        cible={cible}
        prise={prise}
        quandPrise={setPrise}
        quandCible={setCible}
        quandDepot={(vehiculeId, jour) => {
          if (prise === null) return;
          const refuse = refusDepot(prise, jour);
          if (refuse !== null) {
            setRefus(refuse);
            setPrise(null);
            setCible(null);
            return;
          }
          if (reportDemande(prise, jour)) {
            // Le jour change : on DEMANDE. Décaler les dates d'un transport
            // sans le dire serait le pire des deux — l'écran montrerait un
            // camion là où la base dit autre chose, ou l'inverse.
            setReport({ prise, vehiculeId, jour });
            setCible(null);
            return;
          }
          poser(prise, vehiculeId, null);
        }}
        quandLibere={() => {
          if (prise === null || prise.jour === null) return;
          // ON NE RETIRE PAS LE CAMION D'UNE ÉTAPE VALIDÉE. Voir
          // `refusLiberation` : le geste était sans retour.
          const refuse = refusLiberation(prise);
          if (refuse !== null) {
            setRefus(refuse);
            setPrise(null);
            setCible(null);
            return;
          }
          ecrire((j) =>
            affecterAuPlanning(j, {
              ot_id: prise.ot_id,
              jour: prise.jour,
              vehicule_id: null,
            }),
          );
          setPrise(null);
          setCible(null);
        }}
        quandOuvrirOrdre={quandOuvrirOrdre}
        ligneSaisie={ligneSaisie}
        quandLigneSaisie={setLigneSaisie}
        quandRanger={(avant) => {
          if (ligneSaisie === null || etat.nom !== 'pret') return;
          const voulu = ordreApres(etat.planning.vehicules, ligneSaisie, avant);
          setLigneSaisie(null);
          ecrire((j) => rangerLesVehicules(j, voulu));
        }}
        /**
         * RANGER LES BLOCS D'UNE CASE — 16/09/2026.
         *
         * ON ENVOIE LA LISTE COMPLÈTE de la case, jamais le seul déplacement :
         * deux rangs qui se marchent dessus deviennent alors impossibles.
         *
         * LES BLOCS SONT RELUS DEPUIS `blocsParCase`, et non depuis ce que
         * l'écran affiche : la recherche peut en masquer, et ranger sur une
         * liste filtrée effacerait le rang de ceux qu'on ne voit pas.
         */
        quandRangerLaCase={(vehiculeId, jour, avant) => {
          if (prise === null || etat.nom !== 'pret') return;
          const pris = prise;
          const blocs =
            blocsParCase(etat.planning.etapes).get(`${vehiculeId}|${jour}`) ?? [];
          const voulu = ordreDeLaCaseApres(blocs, pris.ot_id, avant);
          const dejaLa = blocs.some((bloc) => bloc.ot_id === pris.ot_id);

          // L'ORDRE EST DÉJÀ DANS LA CASE : on ne fait que le ranger.
          if (dejaLa) {
            setPrise(null);
            setCible(null);
            ecrire((j) => rangerLaCase(j, vehiculeId, jour, voulu));
            return;
          }

          /**
           * IL VIENT D'AILLEURS : ON L'AFFECTE, PUIS ON LE RANGE — 17/09/2026.
           *
           * LES MÊMES GARDES QUE LE DÉPÔT SUR LA CASE, et non un chemin
           * parallèle : un ordre validé ne se déplace pas, et un changement de
           * jour se DEMANDE. Écrire un second chemin d'affectation, c'était
           * deux règles à tenir d'accord — et celle qui se serait relâchée est
           * celle qu'on aurait oubliée.
           */
          const refuse = refusDepot(pris, jour);
          if (refuse !== null) {
            setRefus(refuse);
            setPrise(null);
            setCible(null);
            return;
          }
          if (reportDemande(pris, jour)) {
            /**
             * LE JOUR CHANGE : ON DEMANDE, ET LA PLACE VISÉE EST PERDUE.
             *
             * ASSUMÉ. La boîte de report pose une vraie question — décaler les
             * dates d'un transport — et y répondre « oui, et à la troisième
             * place » mélangerait deux décisions dans un seul bouton. L'ordre
             * arrive au tri par heure ; un second glisser le range.
             */
            setReport({ prise: pris, vehiculeId, jour });
            setCible(null);
            return;
          }

          setPrise(null);
          setCible(null);
          ecrire(async (j) => {
            const pose = await affecterAuPlanning(j, {
              ot_id: pris.ot_id,
              jour: pris.jour,
              vehicule_id: vehiculeId,
            });
            // ON NE RANGE PAS CE QUI N'A PAS ÉTÉ AFFECTÉ : le rang serait
            // orphelin, invisible, et attendrait le jour où l'ordre arrive
            // dans la case pour le placer sans que personne l'ait demandé.
            if (pose.etat !== 'ok') return pose;
            return rangerLaCase(j, vehiculeId, jour, voulu);
          }, pris.tournee);
        }}
      />

      {report === null ? null : (
        <div className="voile-boite" role="presentation">
          <div className="boite-decrochage" role="dialog" aria-modal="true">
            <h3>
              Reporter l’ordre {report.prise.numero} au{' '}
              {colonneDite(report.jour) ?? report.jour} ?
            </h3>
            <p className="quoi-decroche">
              Il est aujourd’hui au{' '}
              {colonneDite(report.prise.jour ?? report.prise.debut) ?? ''}. Toutes ses
              étapes se décalent d’autant : sinon l’écran montrerait un transport d’un
              jour dans la colonne d’un autre.
            </p>
            <div className="pied-boite">
              <button
                className="bouton bouton-discret"
                type="button"
                onClick={() => {
                  setReport(null);
                  setPrise(null);
                }}
              >
                Annuler
              </button>
              <button
                className="bouton bouton-discret"
                type="button"
                onClick={() => {
                  poser(report.prise, report.vehiculeId, null);
                  setReport(null);
                }}
              >
                Affecter sans changer les dates
              </button>
              <button
                className="bouton"
                type="button"
                onClick={() => {
                  poser(report.prise, report.vehiculeId, report.jour);
                  setReport(null);
                }}
              >
                Décaler et affecter
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
